import { CancelTokenSource } from 'axios';
import { isEmpty } from 'lodash';
import moment from 'moment-timezone';
import { FC, ReactNode, createContext, useCallback, useEffect, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import { useAppDispatch } from 'src/app/hooks';
import { CanAccessReceipts, ViewReceiptsCompany } from 'src/components/guards/PermissionsList';
import { completeReceipt } from 'src/features/ReceiptSlice';
import useBoolean from 'src/hooks/useBoolean';
import usePermission from 'src/hooks/usePermissions';
import usePostMessage from 'src/hooks/usePostMessage';
import { GLCodeDropdownData, GlCodesObject } from 'src/services/useGlCodesApi';
import { ReceiptItemNoteItem, ReceiptItemNotesObject } from 'src/services/useReceiptItemNotesApi';
import { ReceiptLineItem, ReceiptLineItemWithSelected } from 'src/services/useReceiptLineItemsApi';
import useUpdateReceiptApi from 'src/services/useUpdateReceiptApi';
import { IReceipt } from 'src/types/Receipt';
import { SetState } from 'src/types/generic';
import { receiptDirtyMessages } from 'src/utils/constants';
import { isEqualString } from 'src/utils/strings';
import { useContextSelector } from 'use-context-selector';
import { PoRelatedDocumentsContext } from '../po_related_documents/PoRelatedDocumentsContext';
import useReceiptDetailsErrors, {
  ReceiptDetailsErrors,
  ReceiptDetailsInfo,
} from './errors/useReceiptDetailsErrors';

interface ReceiptContextType {
  isCompleted: boolean;
  isBlindReceive: boolean;
  isAddLineItems: boolean;
  isViewReceiptsCompany: boolean;
  isAddLineItemButtonDisabled: boolean;
  isInProgress: boolean;
  receiptData: IReceipt;
  getReceiptData: VoidFunction;
  recId: string;
  companyId: string;
  refreshPage: VoidFunction;
  isReceiptLoading: boolean;
  setReceipt: SetState<IReceipt>;
  showScans: boolean;
  setShowScans: SetState<boolean>;
  isReceiptDirty: boolean;
  saveReceiptDetails: (isSave: boolean) => Promise<void>;
  isSaving: boolean;
  receiptLineItems: ReceiptLineItem[];
  lineItemsWithErrorWarningStatus: any[];
  setReceiptLineItems: SetState<ReceiptLineItem[]>;
  isReceiptLineItemsLoading: boolean;
  glCodeOptions: GLCodeDropdownData;
  toggleModal: (modalName: string, data?: any) => void;
  isSaveDisabled: boolean;
  glCodesObject: GlCodesObject;
  receiptItemNotes: ReceiptItemNoteItem[];
  receiptItemNotesObject: ReceiptItemNotesObject;
  fullyCompletedItemNotes: ReceiptItemNoteItem;
  lineItemsErrors: string[];
  errorList: string[];
  errors: ReceiptDetailsErrors;
  infos: ReceiptDetailsInfo;
  infosList: string[];
  isReceiptDetailsDirty: boolean;
  hasErrors: boolean;
  isCurrentDocLinked: boolean;
  isPoOpen: boolean;
  setSelectedLineItemIds: SetState<number[]>;
  selectedLineItemIds: number[];
  hasCompletedLinkedInvoice: boolean;
  updateFormData: (key: string, value: any) => void;
  updateRequestRef: React.MutableRefObject<CancelTokenSource | undefined>;
  isDeleted: boolean;
  getMessages: (messages: string[]) => string[];
  isReceiptActionButtonsDisabled: (disable: boolean) => boolean;
}

export const ReceiptContext = createContext<ReceiptContextType>({} as ReceiptContextType);

interface ReceiptContextProps {
  children: ReactNode;
  receiptData: IReceipt;
  getReceiptData: VoidFunction;
  setReceipt: SetState<IReceipt>;
  companyId: string;
  isReceiptLoading: boolean;
  toggleModal: (modalName: string, data?: any) => void;
  isReceiptDirty: boolean;
  receiptLineItems: ReceiptLineItemWithSelected[];
  setReceiptLineItems: SetState<ReceiptLineItem[]>;
  isReceiptLineItemsLoading: boolean;
  glCodeOptions: GLCodeDropdownData;
  glCodesObject: GlCodesObject;
  getReceiptLineItems: (params?: undefined) => Promise<ReceiptLineItem[]>;
  isReceiptLineItemsDirty: boolean;
  showScans: boolean;
  setShowScans: SetState<boolean>;
  receiptItemNotes: ReceiptItemNoteItem[];
  receiptItemNotesObject: ReceiptItemNotesObject;
  fullyCompletedItemNotes: ReceiptItemNoteItem;
  isInProgress: boolean;
  isBlindReceive: boolean;
  isAddLineItems: boolean;
  isCurrentDocLinked: boolean;
  setSelectedLineItemIds: SetState<number[]>;
  selectedLineItemIds: number[];
}

const ReceiptDetailsContextProvider: FC<ReceiptContextProps> = ({
  children,
  receiptData,
  getReceiptData,
  toggleModal,
  isReceiptDirty,
  receiptLineItems,
  setReceiptLineItems,
  setReceipt,
  isReceiptLineItemsLoading,
  glCodeOptions,
  getReceiptLineItems,
  isReceiptLineItemsDirty,
  glCodesObject,
  isReceiptLoading,
  showScans,
  setShowScans,
  companyId,
  receiptItemNotes,
  receiptItemNotesObject,
  fullyCompletedItemNotes,
  isInProgress,
  isBlindReceive,
  isAddLineItems,
  isCurrentDocLinked,
  setSelectedLineItemIds,
  selectedLineItemIds,
}) => {
  const [searchParams] = useSearchParams();
  const recId = searchParams.get('recid') || '';
  const { updateReceipt, reqRef: updateRequestRef } = useUpdateReceiptApi(recId);
  const { value: isSaving, setTrue: enableIsSaving, setFalse: disableIsSaving } = useBoolean();
  const dispatch = useAppDispatch();
  const receiptCreatedBy = receiptData?.clientID?.toString();
  const isCompleted = useMemo(
    () => receiptData?.receiptStatus?.toLowerCase()?.includes('completed'),
    [receiptData?.receiptStatus]
  );
  const lineItemsErrors: string[] = [];

  const receiptAccess = usePermission({
    permissions: CanAccessReceipts,
    or: true,
    clientId: receiptCreatedBy,
  });

  const refreshPoDetails = useContextSelector(
    PoRelatedDocumentsContext,
    (value) => value.refreshPoDetails
  );

  const refreshPage = useCallback(() => {
    getReceiptData();
    getReceiptLineItems();
    refreshPoDetails();
  }, [getReceiptData, getReceiptLineItems, refreshPoDetails]);

  const isViewReceiptsCompany = usePermission({ permissions: ViewReceiptsCompany });

  const { errors, errorList, infos, infosList, hasErrors, warnings, warningList, hasWarnings } =
    useReceiptDetailsErrors(receiptLineItems, receiptData, glCodeOptions);

  const lineItemsWithErrorWarningStatus: ReceiptLineItemWithSelected[] = useMemo(
    () =>
      receiptLineItems.map((item, index) => ({
        ...item,
        errors: errors.lineItems[index],
        warnings: warnings.lineItems[index],
        informations: infos.lineItems[index],
        hasErrorsOrWarnings:
          Object.values(errors?.lineItems[index])?.flat()?.length > 0 ||
          Object.values(warnings?.lineItems[index])?.flat()?.length > 0 ||
          Object.values(infos?.lineItems[index])?.flat().length > 0 ||
          false,
      })),

    [errors.lineItems, infos.lineItems, receiptLineItems, warnings?.lineItems]
  );
  useEffect(() => {
    if (receiptCreatedBy !== undefined) {
      !receiptAccess && toast.error('No Permission to Access  Receipt');
    }
  }, [receiptAccess, receiptCreatedBy]);

  const isAddLineItemButtonDisabled = useMemo(
    () => !(isInProgress && receiptAccess),
    [isInProgress, receiptAccess]
  );
  const saveReceiptDetails = useCallback(
    async (isSave: boolean) => {
      try {
        enableIsSaving();
        await updateReceipt({
          documentDate: receiptData?.documentDate
            ? moment(receiptData?.documentDate).format('YYYY-MM-DDThh:mm:ss')
            : null,
          deliveryDateTime: receiptData?.deliveryDateTime
            ? moment(receiptData?.deliveryDateTime).format('YYYY-MM-DDThh:mm:ss')
            : null,
          deliveryDriverName: receiptData?.deliveryDriverName,
          deliveryCompanyName: receiptData?.deliveryCompanyName,
          receiptNotes: receiptData?.receiptNotes,
          lineItems: receiptLineItems.map((lineItem) => ({
            ...lineItem,
            receivingReceiptLineItemId: lineItem.receivingReceiptLineItemId
              .toString()
              .endsWith('987654321')
              ? (undefined as unknown as number)
              : lineItem.receivingReceiptLineItemId,
          })),
        });

        if (isSave) {
          toast.success('Receipt saved successfully!');
        }

        if (!isSave) {
          const res = await Promise.all([dispatch(completeReceipt([Number(recId)]))]);
          const completedReceipt = res[0];

          if (isEmpty(completedReceipt.payload)) {
            return;
          }
          toast.success('Receipt completed successfully');
        }

        refreshPage();
        disableIsSaving();
      } catch (err) {
        console.error(err);
        disableIsSaving();
      }
    },
    [
      enableIsSaving,
      updateReceipt,
      receiptData?.documentDate,
      receiptData?.deliveryDateTime,
      receiptData?.deliveryDriverName,
      receiptData?.deliveryCompanyName,
      receiptData?.receiptNotes,
      receiptLineItems,
      refreshPage,
      disableIsSaving,
      dispatch,
      recId,
    ]
  );

  const lineItemsHasErrors = useMemo(
    () =>
      receiptLineItems.some(
        (item: ReceiptLineItem) =>
          !item.itemDescription ||
          !item.uom ||
          item.currentDocQty === undefined ||
          item.currentDocQty.toString().trim() === '' ||
          item.currentDocQty === null
      ),
    [receiptLineItems]
  );

  const isReceiptDetailsSatisfied = useMemo(
    () =>
      errors.documentDate &&
      errors.deliveryDateTime &&
      errors.deliveryDriverName &&
      errors.deliveryCompanyName &&
      isInProgress &&
      receiptAccess,
    [
      errors.deliveryCompanyName,
      errors.deliveryDateTime,
      errors.deliveryDriverName,
      errors.documentDate,
      isInProgress,
      receiptAccess,
    ]
  );

  const isSaveDisabled = useMemo(
    () =>
      !isReceiptDetailsSatisfied ||
      lineItemsHasErrors ||
      (!isReceiptDirty && !isReceiptLineItemsDirty) ||
      hasErrors,
    [
      hasErrors,
      isReceiptDetailsSatisfied,
      isReceiptDirty,
      isReceiptLineItemsDirty,
      lineItemsHasErrors,
    ]
  );

  const isReceiptDetailsDirty = useMemo(
    () => isReceiptDirty || isReceiptLineItemsDirty,
    [isReceiptDirty, isReceiptLineItemsDirty]
  );

  const isPoOpen = useMemo(
    () => isEqualString(receiptData.poStatus, 'open'),
    [receiptData.poStatus]
  );

  const hasCompletedLinkedInvoice: boolean = useMemo(
    () => receiptData?.hasCompletedInvoice,
    [receiptData?.hasCompletedInvoice]
  );

  const updateFormData = useCallback(
    (key: string, value: any) => {
      setReceipt((prev) => ({ ...prev, [key]: value }));
    },
    [setReceipt]
  );

  const isDeleted = useMemo(
    () => isEqualString(receiptData.receiptStatus, 'deleted'),
    [receiptData.receiptStatus]
  );

  const getMessages = useCallback(
    (messages: string[]) =>
      isReceiptDetailsDirty && !hasErrors
        ? receiptDirtyMessages
        : [
            ...errorList,
            ...(isReceiptDetailsDirty ? receiptDirtyMessages : []),
            ...warningList,
            ...messages,
          ],
    [errorList, hasErrors, isReceiptDetailsDirty, warningList]
  );

  const isReceiptActionButtonsDisabled = useCallback(
    (disable: boolean) => disable || hasErrors || hasWarnings || isReceiptDetailsDirty,
    [hasErrors, hasWarnings, isReceiptDetailsDirty]
  );

  const dirtyMessage = useMemo(() => ({ isDirty: isReceiptDetailsDirty }), [isReceiptDetailsDirty]);

  usePostMessage(dirtyMessage);

  return (
    <ReceiptContext.Provider
      value={{
        isCompleted,
        isBlindReceive,
        isViewReceiptsCompany,
        isAddLineItemButtonDisabled,
        receiptData,
        recId,
        isInProgress,
        isAddLineItems,
        getReceiptData,
        setReceipt,
        isReceiptLoading,
        showScans,
        setShowScans,
        isReceiptDirty,
        companyId,
        refreshPage,
        saveReceiptDetails,
        isSaving,
        receiptLineItems,
        setReceiptLineItems,
        isReceiptLineItemsLoading,
        glCodeOptions,
        toggleModal,
        isSaveDisabled,
        glCodesObject,
        receiptItemNotes,
        fullyCompletedItemNotes,
        receiptItemNotesObject,
        lineItemsErrors,
        lineItemsWithErrorWarningStatus,
        errorList,
        errors,
        infos,
        infosList,
        isReceiptDetailsDirty,
        hasErrors,
        isCurrentDocLinked,
        isPoOpen,
        selectedLineItemIds,
        setSelectedLineItemIds,
        hasCompletedLinkedInvoice,
        updateFormData,
        updateRequestRef,
        isDeleted,
        getMessages,
        isReceiptActionButtonsDisabled,
      }}
    >
      {children}
    </ReceiptContext.Provider>
  );
};

export default ReceiptDetailsContextProvider;
